import * as store from "@/store/store";

// NOTE: Used to select prod/test backend on localhost/PR preview sites
const isProdToggled = () => Boolean(store.localStorageGet("env-toggle-prod"));

export const toggleProd = (status?: boolean) => {
  store.localStorageSet("env-toggle-prod", status ?? !isProdToggled());
};

export const env = (): Environment => {
  const { hostname } = window.location;

  if (hostname === "adc.exabel.com") return production;
  if (hostname === "adc-staging.exabel.com") return staging;
  if (hostname === "adc-test.exabel.com") return test;
  return other();
};

// NOTE: Environment config is currently handled in source code because GitHub Enterprise is needed for configuring environments in GitHub.
// If we ever move to GitHub Enterprise we should consider moving this config to environment secrets in GH.
// https://docs.github.com/en/github-ae@latest/actions/deployment/targeting-different-environments/using-environments-for-deployment

type Environment = {
  API_ORIGIN: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_TENANT: string;
  AUTH0_DOMAIN: string;
  /** @deprecated Which backend is currently in use. Use this as a last resort only */
  BACKEND: "production" | "test";
  HEADER_BADGE: string | undefined;
  ADC_DATA_SET: string;
  MARKET_CAP_SIGNAL: string;
  MIXPANEL_DEBUG: boolean;
  MIXPANEL_ENABLED: boolean;
  MIXPANEL_HOST: string;
  MIXPANEL_TOKEN: string;
  /** Enables Sentry if set */
  SENTRY_DSN: string | undefined;
  SENTRY_ENV: string;
  /** Whether the user can choose to log into test/prod backend */
  SHOW_BACKEND_SELECT: boolean;
  VA_DATA_SET: string;
  /**
   * Vendor customer resource names (e.g. `customers/123`) that are allowed to be
   * revealed via the "Click to view" affordance in the Alt Data Universe table.
   */
  REVEALABLE_VENDORS: string[];
};

const production: Environment = {
  API_ORIGIN: "https://endpoints.exabel.com",
  AUTH0_CLIENT_ID: "5uu5AKxZahiCywz8TLCz3HzcnEw3EGtP",
  AUTH0_TENANT: "exabel",
  AUTH0_DOMAIN: "auth.exabel.com",
  BACKEND: "production",
  HEADER_BADGE: undefined,
  ADC_DATA_SET: "dataSets/adc-va.adc-va",
  MARKET_CAP_SIGNAL: "signal:signal:7727",
  MIXPANEL_DEBUG: false,
  MIXPANEL_ENABLED: true,
  MIXPANEL_HOST: "https://tracking.exabel.com",
  MIXPANEL_TOKEN: "4822c039578ce4a27f3a76507a8f1588",
  SENTRY_DSN: "https://df04585f2941400480f0f332421e3b45@sentry.io/1323759",
  SENTRY_ENV: "production",
  SHOW_BACKEND_SELECT: false,
  VA_DATA_SET: "dataSets/va.visible_alpha",
  REVEALABLE_VENDORS: [
    "customers/26",
    "customers/102",
    "customers/184",
    "customers/261",
    "customers/303",
    "customers/316",
    "customers/327",
    "customers/348",
  ],
};

const test: Environment = {
  API_ORIGIN: "https://endpoints-test.exabel.com",
  AUTH0_CLIENT_ID: "wjx3cYZJ2I4LB6XXE7qPbH2bdMJZiLVZ",
  AUTH0_TENANT: "exabel-test",
  AUTH0_DOMAIN: "auth-test.exabel.com",
  BACKEND: "test",
  HEADER_BADGE: "Test",
  ADC_DATA_SET: "dataSets/adc-va.adc-va",
  MARKET_CAP_SIGNAL: "signal:signal:1449",
  VA_DATA_SET: "dataSets/va.va",
  MIXPANEL_DEBUG: true,
  MIXPANEL_ENABLED: true,
  MIXPANEL_HOST: "https://tracking-test.exabel.com",
  MIXPANEL_TOKEN: "f366283ee994725e70aaeff01f06a8e3",
  SENTRY_DSN: undefined,
  SENTRY_ENV: "test",
  SHOW_BACKEND_SELECT: false,
  REVEALABLE_VENDORS: ["customers/52"],
};

const staging: Environment = {
  API_ORIGIN: production.API_ORIGIN,
  AUTH0_CLIENT_ID: production.AUTH0_CLIENT_ID,
  AUTH0_TENANT: production.AUTH0_TENANT,
  AUTH0_DOMAIN: production.AUTH0_DOMAIN,
  BACKEND: production.BACKEND,
  HEADER_BADGE: undefined,
  ADC_DATA_SET: production.ADC_DATA_SET,
  MARKET_CAP_SIGNAL: production.MARKET_CAP_SIGNAL,
  MIXPANEL_DEBUG: true,
  MIXPANEL_ENABLED: true,
  MIXPANEL_HOST: production.MIXPANEL_HOST,
  MIXPANEL_TOKEN: production.MIXPANEL_TOKEN,
  SENTRY_DSN: undefined,
  SENTRY_ENV: test.SENTRY_ENV,
  SHOW_BACKEND_SELECT: false,
  VA_DATA_SET: production.VA_DATA_SET,
  REVEALABLE_VENDORS: production.REVEALABLE_VENDORS,
};

/** Any/all environments that are not test, staging or production, f ex developer machines or PR preview sites.
 * In these environments we show the option to log in to either test or prod backend. The environment config is mostly
 * determined by this choice, with some exceptions.
 */
const other = (): Environment => {
  const isProd = isProdToggled();
  const current: Environment = isProd ? production : test;
  return {
    API_ORIGIN: current.API_ORIGIN,
    AUTH0_CLIENT_ID: isProd ? staging.AUTH0_CLIENT_ID : test.AUTH0_CLIENT_ID,
    AUTH0_TENANT: current.AUTH0_TENANT,
    AUTH0_DOMAIN: current.AUTH0_DOMAIN,
    BACKEND: current.BACKEND,
    HEADER_BADGE: isProd ? "Prod" : "Test",
    ADC_DATA_SET: current.ADC_DATA_SET,
    MARKET_CAP_SIGNAL: current.MARKET_CAP_SIGNAL,
    MIXPANEL_DEBUG: true,
    MIXPANEL_ENABLED: import.meta.env.VITE_DISABLE_TRACKING !== "true",
    MIXPANEL_HOST: current.MIXPANEL_HOST,
    MIXPANEL_TOKEN: test.MIXPANEL_TOKEN,
    SENTRY_DSN: undefined,
    SENTRY_ENV: test.SENTRY_ENV,
    SHOW_BACKEND_SELECT: true,
    VA_DATA_SET: current.VA_DATA_SET,
    REVEALABLE_VENDORS: current.REVEALABLE_VENDORS,
  };
};
